import React from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';

import { beValueDashMetricsRead } from 'actions/sms/beValueDashMetrics.action';
import IMG_BOOKING from 'assets/images/salon/booking.png';
import IMG_MARKETING from 'assets/images/salon/marketing.png';
import IMG_SMS from 'assets/images/salon/sms-reminders.png';
import CurrencySign from 'components/price/CurrencySign.component';
import PLAN_NAME from 'constants/plan.const';
import S from 'constants/string.const';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import { usePrepareReadOnly } from 'hooks/usePrepare.hook';
import { useTranslation } from 'providers/GILT.provider';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { useTheme } from 'providers/Theme.provider';
import { selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import { formatDayToYearOrdinal, formatHourAndMinute } from 'utils/date/format.util';
import { timeSub1Day } from 'utils/date/manipulate.util';
import { nowAsDate } from 'utils/date/now.util';
import C from 'utils/values/constant.util';

import LastMonthInvoice from 'settings/salon/LastMonthInvoice.component';
import SettingsLinksGroup from 'settings/salon/SettingsLinksGroup.component';

import CN from './SettingSummary.module.scss';

const FREE_MARKETING_SMS = C({
  [PLAN_NAME.essential]: 50,
  [PLAN_NAME.advanced]: 500,
  [PLAN_NAME.supercharged]: 2000,
});

const SettingSummary = ({ pricingPlan }) => {
  const { isDarkMode } = useTheme();
  const { salonName } = useSalonBasicInfo();
  const isFromMobile = useIsFromMobileApp();
  const { t } = useTranslation();

  const { qj } = usePrepareReadOnly(beValueDashMetricsRead);

  const smsCount = FREE_MARKETING_SMS[pricingPlan];

  const yesterday = timeSub1Day(nowAsDate());

  //TODO: date localisation: change with formatDateMid when ready
  const formattedDate = formatDayToYearOrdinal(yesterday);
  const formattedTime = formatHourAndMinute(yesterday);

  // Big  metrics number
  const smsSentForFree = qj?.smsRemindersAndConfirmations ?? 0;

  const worth =
    pricingPlan === PLAN_NAME.advanced || pricingPlan === PLAN_NAME.supercharged
      ? qj?.smsReminderDacCost
      : qj?.smsReminderCost;

  const marketingSent = qj?.marketingSent ?? 0;
  const bookingsGeneratedFromMarketing = qj?.resultBookingsRevenue ?? 0;

  const bookingsOnlinePct = qj?.pctOnlineBookings ?? 0;
  const bookingsGeneratedOnline = qj?.ncobValue ?? 0;

  return (
    <div
      data-bem="SettingSummary"
      className={cn({
        [CN.component]: true,
        [CN.moveUp]: worth === 0,
        [CN.dark]: isDarkMode,
      })}
    >
      {/* Section is hidden if sms cost is zero */}
      {worth > 0 && (
        <div data-bem="SettingSummary__value--for--money">
          <div className={CN.title}>
            {S.rocket} {t('staffAndBusiness.summary.title1')}
          </div>
          <div className={CN.subTitle}>
            {t('staffAndBusiness.summary.description', { salon: salonName, date: formattedDate, time: formattedTime })}
          </div>

          <div className={CN.containerMoneyBox}>
            {/* SMS reminders */}
            <div data-bem="SettingSummary__box--reminders" className={cn(CN.box, CN.money)}>
              <div className={CN.textAndImgContainer}>
                <div className={CN.boxName}>
                  {t('staffAndBusiness.summary.box1Title')}
                  <div className={CN.boxAdditionalInfoMobile}>
                    {t('staffAndBusiness.summary.box1DescriptionPart1')}
                    {S.space}
                    <div className={CN.bold}>{t('staffAndBusiness.summary.box1DescriptionPart2')}</div>
                    {S.space}
                    {t('staffAndBusiness.summary.box1DescriptionPart3')}
                  </div>
                </div>

                <img data-bem="SettingSummary__img--reminders" src={IMG_SMS} className={CN.imageBox} alt="" />
              </div>
              <div className={CN.boxContent}>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>{t('staffAndBusiness.summary.freeSms')}</div>
                  <div className={CN.boxValue}>{smsSentForFree}</div>
                </div>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>
                    {t('staffAndBusiness.summary.worth')} {S.partyingFace}
                  </div>
                  <div className={CN.boxValue}>
                    <CurrencySign />
                    {worth ?? 0}
                  </div>
                </div>
                <div className={CN.boxAdditionalInfo}>
                  {t('staffAndBusiness.summary.box1DescriptionPart1')}
                  {S.space}
                  <div className={CN.bold}>{t('staffAndBusiness.summary.box1DescriptionPart2')}</div>
                  {S.space}
                  {t('staffAndBusiness.summary.box1DescriptionPart3')}
                </div>
              </div>
            </div>

            {/* Marketing SMS */}
            <div data-bem="SettingSummary__box--marketing" className={cn(CN.box, CN.marketing)}>
              <div className={CN.textAndImgContainer}>
                <div className={CN.boxName}>
                  {t('staffAndBusiness.summary.box2Title')}
                  <div className={CN.boxAdditionalInfoMobile}>
                    {smsCount ? (
                      <>
                        <div className={CN.bold}>
                          {t('staffAndBusiness.summary.box2DescriptionPart1', { count: smsCount })}
                        </div>
                        {S.space}
                        {t('staffAndBusiness.summary.box2DescriptionPart2')}
                      </>
                    ) : (
                      <div className={CN.bold}>{t('staffAndBusiness.summary.box2DescriptionPart3')}</div>
                    )}
                  </div>
                </div>
                <img data-bem="SettingSummary__img--marketing" src={IMG_MARKETING} className={CN.imageBox} alt="" />
              </div>
              <div className={CN.boxContent}>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>{t('staffAndBusiness.summary.marketingSms')}</div>
                  <div className={CN.boxValue}>{marketingSent}</div>
                </div>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>
                    {t('staffAndBusiness.summary.generatedBookings')} {S.partyingFace}
                  </div>
                  <div className={CN.boxValue}>
                    <CurrencySign />
                    {bookingsGeneratedFromMarketing}
                  </div>
                </div>
                <div className={CN.boxAdditionalInfo}>
                  {smsCount ? (
                    <>
                      <div className={CN.bold}>
                        {t('staffAndBusiness.summary.box2DescriptionPart1', { count: smsCount })}
                      </div>
                      {S.space}
                      {t('staffAndBusiness.summary.box2DescriptionPart2')}
                    </>
                  ) : (
                    <div className={CN.bold}>{t('staffAndBusiness.summary.box2DescriptionPart3')}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Online booking */}
            <div data-bem="SettingSummary__box--booking" className={cn(CN.box, CN.booking)}>
              <div className={CN.textAndImgContainer}>
                <div className={CN.boxName}>
                  {t('staffAndBusiness.summary.box3Title')}
                  <div className={CN.boxAdditionalInfoMobile}>
                    <div className={CN.bold}>{t('staffAndBusiness.summary.box3DescriptionPart1')}</div>
                    {S.space}
                    {t('staffAndBusiness.summary.box3DescriptionPart2')} <CurrencySign />
                    {S.space}
                    {t('staffAndBusiness.summary.box3DescriptionPart3')}
                  </div>
                </div>
                <img data-bem="SettingSummary__img--booking" src={IMG_BOOKING} className={CN.imageBox} alt="" />
              </div>
              <div className={CN.boxContent}>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>{t('staffAndBusiness.summary.onlineMadeBookings')}</div>
                  <div className={CN.boxValue}>{bookingsOnlinePct}%</div>
                </div>
                <div className={CN.boxKey}>
                  <div className={CN.boxKeyTitle}>
                    {t('staffAndBusiness.summary.generatedBookings')} {S.partyingFace}
                  </div>
                  <div className={CN.boxValue}>
                    <CurrencySign />
                    {bookingsGeneratedOnline}
                  </div>
                </div>
                <div className={CN.boxAdditionalInfo}>
                  <div className={CN.bold}>{t('staffAndBusiness.summary.box3DescriptionPart1')}</div>
                  {S.space}
                  {t('staffAndBusiness.summary.box3DescriptionPart2')} <CurrencySign />
                  {S.space}
                  {t('staffAndBusiness.summary.box3DescriptionPart3')}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div
        data-bem="SettingSummary__month--invoice"
        className={cn({
          [CN.layoutMobileHiddenInvoice]: isFromMobile,
        })}
      >
        {!isFromMobile && <LastMonthInvoice />}
      </div>

      <div data-bem="SettingSummary__settingsLinksGroup">
        <SettingsLinksGroup />
      </div>
    </div>
  );
};

const SettingSummaryHoc = connect((state) => ({
  pricingPlan: selectCurrentSalonPricingPlan(state),
}))(SettingSummary);

export default SettingSummaryHoc;
